import { useState } from 'react'

import { Accordion, Button as MantineButton } from '@mantine/core'
import { v1 as uuid } from 'uuid'

import ApplicationAccordionItem from '../components/ApplicationAccordionItem'
import ApplicationAddressInput from '../components/ApplicationAddressInput'
import ApplicationScreenWrapper from '../components/ApplicationScreenWrapper'
import ApplicationStateSelect from '../components/ApplicationStateSelect'
import ApplicationTextInput from '../components/ApplicationTextInput'
import { useFormLeaveGuard } from '../hooks/useLeaveGuard'
import { formatMonthString, formatMonthYear } from '../utils/formatters'
import { useFormContext } from '../utils/FormContext'
import { buildAddressesPayload } from '../utils/stepPayloads'
import { findErrors } from '../utils/validators'

export const CurrentAddressScreen = () => {
  const { form, handleNext, updateLoading, application, backText } =
    useFormContext()
  const { goBack, leaveGuardModal } = useFormLeaveGuard(
    buildAddressesPayload
  )

  // Start with accordion closed if current address has values
  const hasCurrentAddressValues = form.values.addresses[0]?.street !== ''
  const [accordionValue, setAccordionValue] = useState(
    hasCurrentAddressValues ? null : 'item-0'
  )

  return (
    <ApplicationScreenWrapper
      title={'Address Information'}
      description={
        'Please provide any addresses you have lived at in the past 3 years.'
      }
      goBack={goBack}
      updateLoading={updateLoading}
      application={application}
      handleCancel={goBack}
      backText={backText}
      saveButtontext={'Save and continue'}
      handleSubmit={() => {
        setAccordionValue(null)
        handleNext(buildAddressesPayload(form.values))
      }}
    >
      {leaveGuardModal}
      <Accordion
        chevronPosition="left"
        variant="separated"
        value={accordionValue}
        onChange={setAccordionValue}
        radius={'md'}
        classNames={{
          root: 'w-full sm:col-span-3',
          control: 'h-16',
          item: 'bg-transparent border-doubleNickel-gray-300 shadow-sm',
        }}
      >
        <ApplicationAccordionItem
          value={`item-0`}
          key={`item-0`}
          title={
            form.values.addresses[0].street !== ''
              ? form.values.addresses[0].street
              : `Current Address`
          }
          hasErrors={findErrors(form.errors, 'addresses', 0) > 0}
          isCurrent={true}
        >
          <ApplicationAddressInput
            required
            displayStreetOnly
            variant="unstyled"
            label="Street Address"
            placeholder="Current Address"
            value={form.values.addresses[0]}
            error={form.errors[`addresses.${0}.street`]}
            handleChange={(value) => {
              form.setFieldValue(`addresses.${0}.street`, value.street)
              if (value.city)
                form.setFieldValue(`addresses.${0}.city`, value.city)
              if (value.state)
                form.setFieldValue(`addresses.${0}.state`, value.state)
              if (value.zipCode)
                form.setFieldValue(`addresses.${0}.zipCode`, value.zipCode)
              if (value.country)
                form.setFieldValue(`addresses.${0}.country`, value.country)
            }}
          />
          <div className="flex w-full flex-row gap-2 sm:gap-4">
            <ApplicationTextInput
              label="City"
              placeholder={'City'}
              required
              value={form.values.addresses[0].city}
              error={form.errors[`addresses.${0}.city`]}
              onChange={(event) => {
                form.setFieldValue(`addresses.${0}.city`, event.target.value)
              }}
            />
            <ApplicationStateSelect
              label="State"
              placeholder={'State'}
              required
              value={form.values.addresses[0].state}
              error={form.errors[`addresses.${0}.state`]}
              onChange={(value) => {
                form.setFieldValue(`addresses.${0}.state`, value)
              }}
            />
          </div>
          <div className="flex w-full flex-row gap-2 sm:gap-4">
            <ApplicationTextInput
              label="Country"
              placeholder={'Country'}
              required
              value={form.values.addresses[0].country}
              error={form.errors[`addresses.${0}.country`]}
              onChange={(event) => {
                form.setFieldValue(`addresses.${0}.country`, event.target.value)
              }}
            />
            <ApplicationTextInput
              label="Postal Code"
              placeholder={'Postal Code'}
              required
              value={form.values.addresses[0].zipCode}
              error={form.errors[`addresses.${0}.zipCode`]}
              onChange={(event) => {
                form.setFieldValue(`addresses.${0}.zipCode`, event.target.value)
              }}
            />
          </div>
          <div className="flex w-full flex-row gap-2 sm:gap-4">
            <ApplicationTextInput
              label="Start Date"
              pattern="\d*"
              placeholder={'MM/YYYY'}
              required
              value={formatMonthString(form.values.addresses[0].startDate)}
              error={form.errors[`addresses.${0}.startDate`]}
              onChange={(event) => {
                formatMonthYear(`addresses.${0}.startDate`, event, form)
              }}
            />
          </div>
        </ApplicationAccordionItem>

        {form.values.addresses.slice(1).map((address, index) => {
          return (
            <ApplicationAccordionItem
              value={`item-${index + 1}`}
              key={address.id}
              title={
                form.values.addresses[index + 1].street !== ''
                  ? form.values.addresses[index + 1].street
                  : `Address ${index + 2}`
              }
              hasErrors={findErrors(form.errors, 'addresses', index + 1) > 0}
              isCurrent={false}
              handleDelete={() => form.removeListItem('addresses', index + 1)}
            >
              <ApplicationAddressInput
                required
                variant="unstyled"
                displayStreetOnly
                label="Street Address"
                placeholder="Street Address"
                value={form.values.addresses[index + 1]}
                error={form.errors[`addresses.${index + 1}.street`]}
                handleChange={(value) => {
                  form.setFieldValue(
                    `addresses.${index + 1}.street`,
                    value.street
                  )
                  if (value.city)
                    form.setFieldValue(
                      `addresses.${index + 1}.city`,
                      value.city
                    )
                  if (value.state)
                    form.setFieldValue(
                      `addresses.${index + 1}.state`,
                      value.state
                    )
                  if (value.zipCode)
                    form.setFieldValue(
                      `addresses.${index + 1}.zipCode`,
                      value.zipCode
                    )
                  if (value.country)
                    form.setFieldValue(
                      `addresses.${index + 1}.country`,
                      value.country
                    )
                }}
              />
              <div className="flex w-full flex-row gap-2 sm:gap-4">
                <ApplicationTextInput
                  label="City"
                  placeholder={'City'}
                  required
                  value={form.values.addresses[index + 1].city}
                  error={form.errors[`addresses.${index + 1}.city`]}
                  onChange={(event) => {
                    form.setFieldValue(
                      `addresses.${index + 1}.city`,
                      event.target.value
                    )
                  }}
                />

                <ApplicationStateSelect
                  label="State"
                  placeholder={'State'}
                  required
                  value={form.values.addresses[index + 1].state}
                  error={form.errors[`addresses.${index + 1}.state`]}
                  onChange={(value) => {
                    form.setFieldValue(`addresses.${index + 1}.state`, value)
                  }}
                />
              </div>
              <div className="flex w-full flex-row gap-2 sm:gap-4">
                <ApplicationTextInput
                  label="Country"
                  placeholder={'Country'}
                  required
                  value={form.values.addresses[index + 1].country}
                  error={form.errors[`addresses.${index + 1}.country`]}
                  onChange={(event) => {
                    form.setFieldValue(
                      `addresses.${index + 1}.country`,
                      event.target.value
                    )
                  }}
                />
                <ApplicationTextInput
                  label="Postal Code"
                  placeholder={'Postal Code'}
                  required
                  value={form.values.addresses[index + 1].zipCode}
                  error={form.errors[`addresses.${index + 1}.zipCode`]}
                  onChange={(event) => {
                    form.setFieldValue(
                      `addresses.${index + 1}.zipCode`,
                      event.target.value
                    )
                  }}
                />
              </div>

              <ApplicationTextInput
                label="Start Date"
                pattern="\d*"
                placeholder={'MM/YYYY'}
                required
                value={formatMonthString(
                  form.values.addresses[index + 1].startDate
                )}
                error={form.errors[`addresses.${index + 1}.startDate`]}
                onChange={(event) => {
                  formatMonthYear(
                    `addresses.${index + 1}.startDate`,
                    event,
                    form
                  )
                }}
              />
              <ApplicationTextInput
                label="End Date"
                pattern="\d*"
                placeholder={'MM/YYYY'}
                required
                value={formatMonthString(
                  form.values.addresses[index + 1].endDate
                )}
                error={form.errors[`addresses.${index + 1}.endDate`]}
                onChange={(event) => {
                  formatMonthYear(`addresses.${index + 1}.endDate`, event, form)
                }}
              />
            </ApplicationAccordionItem>
          )
        })}
      </Accordion>

      <div className={`col-span-3 grid-cols-subgrid`}>
        <MantineButton
          size="sm"
          variant="light"
          classNames={{
            root: 'h-11 rounded-lg border border-doubleNickel-brand-500 bg-doubleNickel-brand-25 text-sm font-semibold text-doubleNickel-brand-700 hover:bg-doubleNickel-brand-50',
          }}
          onClick={() => {
            setAccordionValue(`item-${form.values.addresses.length}`)
            form.setFieldValue('addresses', [
              ...form.values.addresses,
              {
                id: uuid(),
                street: '',
                city: '',
                state: '',
                zipCode: '',
                startDate: '',
                endDate: '',
              },
            ])
          }}
        >
          Add Address
        </MantineButton>
      </div>
    </ApplicationScreenWrapper>
  )
}
